import { Alert, AlertDescription, Button, Card, CardContent, CardHeader, CardTitle } from '@1trading/react';
import { ArrowLeft, SearchX } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DataTable } from '@/components/data-table/data-table';
import { EmptyState } from '@/components/feedback/empty-state';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { CopyButton } from '@/components/ui/copy-button';
import { LoadingPanel } from '@/components/ui/loading-panel';
import { MAX_RANGE_DAYS } from '@/features/abuse/abuse-filters';
import { SUBJECT_TYPE_SINGULAR } from '@/features/abuse/abuse-meta';
import { EVENT_COLUMNS } from '@/features/abuse/events/event-columns';
import { LinkedSubjects } from '@/features/abuse/subject/linked-subjects';
import { SubjectHeader } from '@/features/abuse/subject/subject-header';
import { SubjectSearch } from '@/features/abuse/subject-search';
import { useAbuseSubject } from '@/hooks/queries/compliance/use-abuse';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { getErrorMessage, isNotFoundError } from '@/lib/api/error';
import { userConsolePath } from '@/lib/paths';
import type { AbuseSubjectKey } from '@/services/api/abuse';

const EVENTS_PAGE_SIZE = 25;

export const SubjectPage = ({ subjectKey }: { subjectKey: AbuseSubjectKey }) => {
  useDocumentTitle(`${SUBJECT_TYPE_SINGULAR[subjectKey.subjectType]} ${subjectKey.subjectId}`);

  const query = useAbuseSubject(subjectKey);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground" to="/compliance/abuse">
          <ArrowLeft className="size-4" />
          Back to abuse monitoring
        </Link>
        <SubjectSearch />
      </div>
      {query.isPending ? (
        <LoadingPanel />
      ) : query.isError ? (
        isNotFoundError(query.error) ? (
          <EmptyState
            action={
              subjectKey.subjectType === 'user' ? (
                <Button asChild variant="outline">
                  <Link to={userConsolePath(subjectKey.subjectId)}>Open user console</Link>
                </Button>
              ) : null
            }
            icon={SearchX}
            title="No abuse recorded"
          >
            {SUBJECT_TYPE_SINGULAR[subjectKey.subjectType]} <span className="break-all font-mono text-foreground">{subjectKey.subjectId}</span>
            <CopyButton className="-my-1 align-middle" label={`Copy ${subjectKey.subjectId}`} value={subjectKey.subjectId} /> has no live score and no events in the last{' '}
            {MAX_RANGE_DAYS} days.
          </EmptyState>
        ) : (
          <QueryErrorBanner message={`Failed to load subject: ${getErrorMessage(query.error)}`} onRetry={() => query.refetch()} />
        )
      ) : (
        <>
          <SubjectHeader detail={query.data} subjectKey={subjectKey} />
          {query.data.isEventsCapped ? (
            <Alert>
              <AlertDescription>Showing the latest {query.data.events.length} events only, so linked subjects may be incomplete.</AlertDescription>
            </Alert>
          ) : null}
          <LinkedSubjects detail={query.data} />
          <Card>
            <CardHeader>
              <CardTitle>Events</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={EVENT_COLUMNS}
                data={query.data.events}
                emptyMessage="No events retained for this subject"
                getRowId={(row, index) => `${row.occurredAt}-${index}`}
                pageSize={EVENTS_PAGE_SIZE}
                surface={false}
              />
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
SubjectPage.displayName = 'SubjectPage';
